import React, { useEffect, useMemo, useState } from "react";

const ICON_BADGE_SIZE = 44;
const ICON_BADGE_SIZE_ACTIVE = 60;
const TITLE_FONT_SIZE_MAX = 17;
const TITLE_FONT_SIZE_MIN = 10;
const TITLE_FONT_FAMILY = "Montserrat, sans-serif";
const TITLE_FONT_WEIGHT = 700;
const CARD_HORIZONTAL_PADDING = 40; // 20px each side — matches the card's own padding
// Desktop card width (matches TILE_SIZE in OrbitServicesSlider.jsx). A card
// is only ever widened up to this — never wider than a normal desktop
// card — and `size < DESKTOP_CARD_SIZE` is also how this file detects
// "we're on a shrunk/responsive card" without a separate matchMedia check:
// getEffectiveTileSize only ever returns less than its own TILE_SIZE cap
// when the container is narrower than a full desktop width.
const DESKTOP_CARD_SIZE = 260;
// Rough average glyph width for bold Montserrat, as a fraction of
// font-size — used only as the SSR/no-canvas fallback for measureTextWidth
// below (real browsers measure actual text via canvas instead).
const AVG_GLYPH_WIDTH_EM = 0.58;

// Real canvas text measurement (not a character-count estimate) so title
// sizing/fitting is accurate for the actual rendered font, not a guess.
// Cached module-level — a single 1x1 canvas is reused for every
// measurement rather than creating one per call.
let measureCtx;
function getMeasureContext() {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement("canvas").getContext("2d");
  } catch (e) {
    measureCtx = null;
  }
  return measureCtx;
}

function measureTextWidth(text, fontSize) {
  const ctx = getMeasureContext();
  if (!ctx) return text.length * fontSize * AVG_GLYPH_WIDTH_EM;
  ctx.font = `${TITLE_FONT_WEIGHT} ${fontSize}px ${TITLE_FONT_FAMILY}`;
  return ctx.measureText(text).width;
}

// Canvas text width scales linearly with font-size for a fixed font/text,
// so measuring once at a large reference size and dividing gives an exact
// "width per 1px of font-size" — solving "the font size where this text's
// width equals a target width" becomes a division, not a search loop.
const REFERENCE_FONT_SIZE = 100;
function widthPerFontPixel(text) {
  if (!text) return 0;
  return measureTextWidth(text, REFERENCE_FONT_SIZE) / REFERENCE_FONT_SIZE;
}

// Largest font size (capped at TITLE_FONT_SIZE_MAX) that fits the title's
// longest word on one line within the card's usable width. Cards are wide
// enough on desktop that this always resolves to the max — it only
// actually shrinks the font on narrow/mobile cards.
function getTitleFontSize(title, cardSize) {
  const longestWord = title
    .split(" ")
    .reduce((longest, word) => (word.length > longest.length ? word : longest), "");
  const perPixel = widthPerFontPixel(longestWord);
  if (perPixel <= 0) return TITLE_FONT_SIZE_MAX;
  const usableWidth = Math.max(cardSize - CARD_HORIZONTAL_PADDING, 0);
  const fitFontSize = usableWidth / perPixel;
  return Math.max(TITLE_FONT_SIZE_MIN, Math.min(TITLE_FONT_SIZE_MAX, fitFontSize));
}

// For a title with MORE than two words, finds the two-line word split
// (grouping consecutive words, never splitting a word) that minimizes the
// wider of the two resulting lines at the given font size, and returns
// that width. A title with two words or fewer already fits two lines in
// the worst case (one word per line), so this only has work to do — and
// is only called — for 3+-word titles. Returns 0 when there's nothing to
// widen for.
function getTwoLineFitWidth(title, fontSize) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return 0;

  let best = Infinity;
  for (let split = 1; split < words.length; split++) {
    const line1 = words.slice(0, split).join(" ");
    const line2 = words.slice(split).join(" ");
    const widest = Math.max(measureTextWidth(line1, fontSize), measureTextWidth(line2, fontSize));
    if (widest < best) best = widest;
  }
  return best;
}

// Only on shrunk/responsive cards (never on desktop — see DESKTOP_CARD_SIZE
// above), widens a 3+-word title's card just enough to keep it to two
// lines instead of spilling to a third, capped at the normal desktop card
// width so a mobile card never grows past a desktop one. Two-pass: first
// find how wide the card needs to be for a two-line layout at the largest
// font (the width we're solving for), then re-derive the actual font size
// for that final width via getTitleFontSize (which may still trim it
// further if the single longest word doesn't fit at the max font).
function getResponsiveCardLayout(title, cardSize) {
  if (cardSize >= DESKTOP_CARD_SIZE) {
    return { cardWidth: cardSize, fontSize: getTitleFontSize(title, cardSize) };
  }
  const twoLineFitWidth = getTwoLineFitWidth(title, TITLE_FONT_SIZE_MAX);
  const cardWidth =
    twoLineFitWidth > 0
      ? Math.min(DESKTOP_CARD_SIZE, Math.max(cardSize, twoLineFitWidth + CARD_HORIZONTAL_PADDING))
      : cardSize;
  return { cardWidth, fontSize: getTitleFontSize(title, cardWidth) };
}

export default function OrbitTile({
  title,
  mobileTitle,
  desc,
  related,
  Icon,
  x,
  y,
  scale,
  zIndex,
  isActive,
  hoverEnabled,
  size,
  onClick,
}) {
  const [isHovered, setIsHovered] = useState(false);

  // `size < DESKTOP_CARD_SIZE` is this file's existing "we're on a
  // shrunk/responsive card" signal (see getResponsiveCardLayout below) —
  // reused here so a service's optional shorter mobileTitle only ever
  // displays on the same "responsive version" as every other mobile-only
  // behavior in this component, never on desktop.
  const displayTitle = size < DESKTOP_CARD_SIZE && mobileTitle ? mobileTitle : title;

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  // The "in focus" highlight is driven purely by isActive (the orbit's
  // front tile), not by DOM focus — a mouse click both jumps the orbit AND
  // leaves the clicked element focused, and that focus otherwise lingers
  // as a visible ring on a tile that's no longer front once the orbit
  // keeps rotating. Real keyboard focus still gets a ring, but via the
  // ".orbit-tile-btn:focus-visible" rule in OrbitServicesSlider (CSS
  // :focus-visible only engages for keyboard navigation, not a mouse
  // click) — inline styles can't express :focus-visible, hence the class.
  //
  // Glow is the exact box-shadow the Hero "Start Your Project" button uses
  // on hover (see hero-btn-primary in Hero.jsx) — hover, active/front, and
  // a click all produce the same glow so the card's "in focus" treatment
  // reads as the same interaction language as the site's primary CTA.
  const cardBoxShadow =
    (hoverEnabled && isHovered) || isActive
      ? "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)"
      : "0 8px 24px rgba(0,0,0,0.28)";

  // canvas.measureText silently measures with a fallback system font,
  // giving narrower-than-real widths, if the target web font (Montserrat
  // Bold here) hasn't finished loading yet at the moment it's called —
  // easily true on first mount. Re-running the calculation once
  // document.fonts.ready resolves (a one-time event, not a poll) catches
  // that case; without this, a title measured too early stays cached at
  // the wrong (too-narrow) width for the component's whole lifetime, since
  // fonts becoming ready doesn't change `title` or `size`.
  const [fontsReadyTick, setFontsReadyTick] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReadyTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Memoized because this component re-renders every orbit rotation frame
  // (via the parent's setTiles) while `displayTitle` and `size` are
  // unchanged across almost all of those frames — recomputing two canvas
  // measurements 60 times a second per tile for no reason is wasted work.
  // fontsReadyTick is a deliberate exception: it only ever changes once.
  const { cardWidth, fontSize: titleFontSize } = useMemo(
    () => getResponsiveCardLayout(displayTitle, size),
    [displayTitle, size, fontsReadyTick]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className="orbit-tile-btn"
      aria-current={isActive ? "true" : undefined}
      // The card's own content (below) is what expands/collapses as this
      // tile becomes the front tile — unlike a separate accordion row with
      // no click target of its own, this button IS the thing that expands,
      // so aria-expanded is valid ARIA here (supported on role="button")
      // and accurately describes what activating it does.
      aria-expanded={isActive}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => hoverEnabled && setIsHovered(true)}
      onMouseLeave={() => hoverEnabled && setIsHovered(false)}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        // Position/scale only — no CSS transition here. This transform is
        // rewritten by the orbit's 60fps GSAP loop; a transitioned
        // property fighting a per-frame update stalls in an inconsistent
        // in-between state and, worse, smears the tap-to-jump tween's own
        // eased ~0.6s move into a longer, doubled-up ease. The hover
        // "lift" lives entirely on the inner card below instead.
        transform: `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale})`,
        zIndex,
        cursor: "pointer",
        outline: "none",
      }}
    >
      <div
        className="orbit-tile-card"
        style={{
          width: cardWidth,
          boxSizing: "border-box",
          padding: "22px 20px",
          borderRadius: 20,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          background: "linear-gradient(160deg, rgba(32,58,55,0.55), rgba(9,18,17,0.6))",
          border: `1px solid ${isActive ? "rgba(194,232,225,0.45)" : "rgba(123,181,177,0.16)"}`,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          boxShadow: cardBoxShadow,
          transform: hoverEnabled && isHovered ? "translateY(-4px)" : "translateY(0)",
          transition: hoverEnabled
            ? "border-color 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease"
            : "border-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        <div
          style={{
            width: isActive ? ICON_BADGE_SIZE_ACTIVE : ICON_BADGE_SIZE,
            height: isActive ? ICON_BADGE_SIZE_ACTIVE : ICON_BADGE_SIZE,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "radial-gradient(circle at 35% 30%, #C2E8E1, #5B9490 75%)",
            boxShadow:
              hoverEnabled && isHovered
                ? "0 0 24px rgba(166,210,206,0.65)"
                : "0 0 14px rgba(166,210,206,0.35)",
            transition: hoverEnabled
              ? "box-shadow 0.25s ease, width 0.3s ease, height 0.3s ease"
              : "width 0.3s ease, height 0.3s ease",
          }}
        >
          {/* +40% (20 -> 28) when active, so the icon carries real weight
              once the enlarged badge brings it into focus. */}
          {Icon ? (
            <Icon size={isActive ? 28 : 20} color="#0A1512" strokeWidth={1.75} />
          ) : null}
        </div>

        <h3
          className="orbit-tile-title"
          style={{
            // Icon-to-title gap raised 14 -> 17px (+20%) to give the title
            // block a bit more breathing room below the badge, since the
            // title-to-description gap right below it was tightened.
            margin: "17px 0 0",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            fontSize: titleFontSize,
            letterSpacing: "-0.02em",
            color: isActive ? "#EAF6F3" : "#C2E8E1",
            // getTitleFontSize already sizes the font so the longest word
            // normally fits on its own line — wordBreak stays "normal" so
            // a word is never split mid-letter in that common case.
            // overflowWrap: "break-word" is only the last-resort fallback
            // for a card so narrow even TITLE_FONT_SIZE_MIN doesn't fit —
            // it keeps the title contained inside the card's edge (the
            // original bug) rather than letting it overflow uncontained.
            wordBreak: "normal",
            overflowWrap: "break-word",
            transition: "color 0.3s ease, font-size 0.2s ease",
          }}
        >
          {displayTitle}
        </h3>

        {/*
          Description and related-tags stay mounted at all times —
          collapsed via maxHeight/opacity, never display:none or
          unmounting — so search crawlers and screen readers can reach
          every service's full detail regardless of which tile the orbit
          currently has in front. Don't "optimize" either into a
          conditional render.

          On mobile (<=767px) these never expand at all, even when
          isActive — see the ".orbit-tile-desc, .orbit-tile-tags" media
          query in OrbitServicesSlider.jsx, which forces them to stay
          collapsed with !important (needed to beat these inline styles).
          Desktop behavior below is unchanged.
        */}
        <p
          className="orbit-tile-desc"
          style={{
            // Tightened from 10px so the description reads as part of the
            // same block as the title instead of floating below it.
            margin: isActive ? "4px 0 0" : "0",
            fontFamily: "system-ui, sans-serif",
            fontWeight: 300,
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "#A6D2CE",
            maxHeight: isActive ? 240 : 0,
            opacity: isActive ? 1 : 0,
            overflow: "hidden",
            transition: "max-height 0.35s ease, opacity 0.3s ease, margin 0.35s ease",
          }}
        >
          {desc}
        </p>

        {related && related.length > 0 ? (
          <div
            className="orbit-tile-tags"
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 8,
              marginTop: isActive ? 14 : 0,
              maxHeight: isActive ? 120 : 0,
              opacity: isActive ? 1 : 0,
              overflow: "hidden",
              transition: "max-height 0.35s ease, opacity 0.3s ease, margin-top 0.35s ease",
            }}
          >
            {related.map((tag) => (
              <span
                key={tag}
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(123,181,177,0.35)",
                  background: "rgba(20,38,36,0.5)",
                  color: "#BFE0DB",
                  fontFamily: "system-ui, sans-serif",
                  fontWeight: 300,
                  fontSize: 10.5,
                  whiteSpace: "nowrap",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
